import "./style.scss";
import { FC, useState, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import CustomSwitch from "../../../../CustomUtils/CustomSwitch/CustomSwitch";
import { AOPMaltaAdmissionData, AOPPolandAdmissionData, AOPUkraineAdmissionData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { Nationalities } from "@/src/constants/globalConsts";
import { ADMISSION_LETTER_BASIC_REGISTRY } from "../admissionLetterBasicRegistry";
import type { AdmissionOfficeCountry } from "../../../admissionOfficeCountry";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionTaskPrefill } from "@/src/hooks/useAdmissionTaskPrefill";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import {
  admissionLetterTaskPrefillMapping,
  syncCourseAndNationalityAutocompletes,
} from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
interface IAOPFormValues {
  whose_doc: string;
  addInvoice?: boolean;
  currentDate: string;
  fullName: string;
  fullNameNat?: string; // new
  dateOfBirth: string;
  nationality: string;
  formOfEducation?: string; // new
  admissionSemester?: string; // new
  intake?: string; // new
  category?: string; // new
  course: string;
  selectedCourse: string;
  mediumOfInstruction: string;
  duration: string;
  tuitionFee?: string; // new
  medicalInsurance?: string; // new
  residencePermit?: string; // new
  otherExpenses?: string; // new
  acommodation?: string; // new
  admissionFee?: string; // new
  transfer?: string; // new
  completeOneYear?: string; // new
  invoiceBody: string;
  signature: string;
  coursePrefix: string;
  task_id?: string;
  student_id?: string;
}

interface AOPConfigProps {
  country: AdmissionOfficeCountry;
  dataForGeneration: AOPMaltaAdmissionData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaAdmissionData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaAdmissionData>>;
  setShowInvoiceInPreview: React.Dispatch<React.SetStateAction<boolean>>;
}

type FeeFieldKey =
  | "tuitionFee"
  | "medicalInsurance"
  | "residencePermit"
  | "otherExpenses"
  | "acommodation"
  | "admissionFee"
  | "transfer"
  | "completeOneYear";

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const FEE_LINE_CONFIG: Array<{ field: FeeFieldKey; labels: string[] }> = [
  { field: "tuitionFee", labels: ["Tuition Fee"] },
  { field: "medicalInsurance", labels: ["Medical Insurance"] },
  { field: "residencePermit", labels: ["Temporary Residence Permission", "Residence Permit"] },
  { field: "otherExpenses", labels: ["Other Expenses"] },
  { field: "acommodation", labels: ["Accommodation confirmation letter", "Accommodation"] },
  { field: "admissionFee", labels: ["Admission Fee"] },
  { field: "transfer", labels: ["Transfer Charges", "Transfer"] },
  { field: "completeOneYear", labels: ["Complete first year expenses", "Complete One Year"] },
];

const SUPPORTED_CURRENCIES = ["EUR", "USD", "USDT", "GBP", "AED"] as const;
const CURRENCY_PATTERN = SUPPORTED_CURRENCIES.join("|");

const buildLabelRegex = (label: string) => {
  const escapedLabel = escapeRegExp(label);
  return new RegExp(`(${escapedLabel}[\\s\\S]{0,260}?:\\s*(?:<[^>]*>\\s*)*)([\\d\\s.,]+)(\\s*(?:${CURRENCY_PATTERN})[^<\\n\\r]*)`, "i");
};

const replaceFeeValueInContent = (html: string, labels: string[], nextValue: string) => {
  if (!html || !nextValue.trim()) return html;
  let nextHtml = html;
  labels.forEach((label) => {
    const matcher = buildLabelRegex(label);
    nextHtml = nextHtml.replace(matcher, `$1${nextValue.trim()}$3`);
  });
  return nextHtml;
};

const extractFeeValueFromContent = (html: string, labels: string[]) => {
  if (!html) return "";
  for (const label of labels) {
    const matcher = buildLabelRegex(label);
    const match = html.match(matcher);
    if (match?.[2]) {
      return match[2].trim();
    }
  }
  return "";
};

const parseMoneyValue = (rawValue?: string) => {
  if (!rawValue) return 0;
  const cleaned = rawValue.replace(/[^\d.,]/g, "").trim();
  if (!cleaned) return 0;
  const normalized = cleaned.replace(/\s+/g, "").replace(",", ".");
  const numeric = Number.parseFloat(normalized);
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatMoneyValue = (value: number) => {
  if (!Number.isFinite(value)) return "";
  if (Math.abs(value % 1) < 0.001) {
    return Math.round(value)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }
  return value.toFixed(2).replace(".", ",");
};

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ country, dataForGeneration, setDataForGeneration, setBodyForPrev, setShowInvoiceInPreview }) => {
  const registryEntry = ADMISSION_LETTER_BASIC_REGISTRY[country];
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      addInvoice: false,
      invoiceBody: "",
      currentDate: formatDateForInput(today),
      fullName: "",
      fullNameNat: "",
      dateOfBirth: "",
      nationality: "",
      course: "",
      selectedCourse: "",
      mediumOfInstruction: "",
      duration: "",
      task_id: "",
      student_id: "",
    },
  });
  const dispatch = useDispatch();
  const generatedDoc = useAdmissionGeneratedDocumentActions({
    getValues,
    studentIdFormField: "student_id",
  });
  const [content, setContent] = useState<string>("");
  const [invoiceContent, setInvoiceContent] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const isSyncingFromInputs = useRef(false);
  const isSyncingFromEditor = useRef(false);

  const [isPreviewReady, setIsPreviewReady] = useState(false);

  const course = watch("course");
  const invoiceBody = watch("invoiceBody");
  const addInvoice = watch("addInvoice");
  const tuitionFee = watch("tuitionFee");
  const medicalInsurance = watch("medicalInsurance");
  const residencePermit = watch("residencePermit");
  const otherExpenses = watch("otherExpenses");
  const acommodation = watch("acommodation");
  const admissionFee = watch("admissionFee");
  const transfer = watch("transfer");
  const completeOneYear = watch("completeOneYear");

  useEffect(() => {
    setShowInvoiceInPreview(!!addInvoice);
    if (!addInvoice) {
      setBodyForPrev((prev) => ({ ...prev, invoiceBody: undefined }));
    }
  }, [addInvoice, setShowInvoiceInPreview, setBodyForPrev]);

  // AUTOCOMPLETE ↓
  const signatureAuto = useAutocomplete(Object.keys(registryEntry.signatures));
  const offeredCourseAuto = useAutocomplete(Object.keys(registryEntry.admissionCourses));
  const invoiceBodyAuto = useAutocomplete(Object.keys(registryEntry.invoiceCourses));
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country,
    documentSlug: "admission",
    mapping: admissionLetterTaskPrefillMapping,
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
      syncCourseAndNationalityAutocompletes(payload, offeredCourseAuto, nationalitiesAuto, whoseDocAuto);
    },
  });

  // EDITORS
  useEffect(() => {
    if (!course) {
      setContent("");
      return;
    }
    const courseSettings = ADMISSION_LETTER_BASIC_REGISTRY[country].admissionCourses[course];
    if (courseSettings) {
      setContent(courseSettings.tinyMCEContent);
      setValue("selectedCourse", courseSettings.selected_course);
      setValue("mediumOfInstruction", courseSettings.medium_of_instruction);
      setValue("duration", courseSettings.duration);
      setTitle(courseSettings.title);
    } else {
      setContent("");
    }
  }, [course, setValue, country]);

  useEffect(() => {
    if (!invoiceBody) {
      setInvoiceContent("");
      return;
    }
    const invoiceSettings = ADMISSION_LETTER_BASIC_REGISTRY[country].invoiceCourses[invoiceBody];
    setInvoiceContent(invoiceSettings ? invoiceSettings.tinyMCEContent : "");
  }, [invoiceBody, country]);

  useEffect(() => {
    const total =
      parseMoneyValue(tuitionFee) +
      parseMoneyValue(medicalInsurance) +
      parseMoneyValue(residencePermit) +
      parseMoneyValue(otherExpenses) +
      parseMoneyValue(acommodation) +
      parseMoneyValue(admissionFee) +
      parseMoneyValue(transfer);

    const formattedTotal = formatMoneyValue(total);
    const currentTotal = getValues("completeOneYear")?.trim() || "";
    if (formattedTotal && currentTotal !== formattedTotal) {
      setValue("completeOneYear", formattedTotal, { shouldDirty: true });
    }
  }, [tuitionFee, medicalInsurance, residencePermit, otherExpenses, acommodation, admissionFee, transfer, getValues, setValue]);

  useEffect(() => {
    if (!content || isSyncingFromEditor.current) return;

    const currentValues: Record<FeeFieldKey, string | undefined> = {
      tuitionFee,
      medicalInsurance,
      residencePermit,
      otherExpenses,
      acommodation,
      admissionFee,
      transfer,
      completeOneYear,
    };

    let nextContent = content;
    FEE_LINE_CONFIG.forEach(({ field, labels }) => {
      const value = currentValues[field];
      if (!value?.trim()) return;
      nextContent = replaceFeeValueInContent(nextContent, labels, value);
    });

    if (nextContent !== content) {
      isSyncingFromInputs.current = true;
      setContent(nextContent);
    }
  }, [content, tuitionFee, medicalInsurance, residencePermit, otherExpenses, acommodation, admissionFee, transfer, completeOneYear]);

  const generateNewDocument = async (values: IAOPFormValues) => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      const signatureKey = values.signature;
      const signatureValue = registryEntry.signatures[signatureKey];
      let response;
      if (country === "malta") {
        if (addInvoice) {
          response = await admissionOfficeRequests.createAdmissionInvoiceDoc(dataForGeneration);
        } else {
          response = await admissionOfficeRequests.createAdmissionDoc(dataForGeneration, signatureValue);
        }
      } else if (country === "poland") {
        const data = dataForGeneration as AOPPolandAdmissionData;
        if (addInvoice) {
          response = await admissionOfficeRequests.createPolandAdmissionInvoiceDoc(data);
        } else {
          response = await admissionOfficeRequests.createPolandAdmissionDoc(data, signatureValue);
        }
      } else {
        const data = dataForGeneration as AOPUkraineAdmissionData;
        if (addInvoice) {
          response = await admissionOfficeRequests.createUkraineAdmissionInvoiceDoc(data);
        } else {
          response = await admissionOfficeRequests.createUkraineAdmissionDoc(data, signatureValue);
        }
      }
      console.log("response", response);
      generatedDoc.onDocumentGenerated(response);
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      coursePrefix: watch("coursePrefix"),
      selectedCourse: watch("selectedCourse"),
      duration: watch("duration"),
      fullName: watch("fullName"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      nationality: watch("nationality"),
      title: title,
      whose_doc: watch("whose_doc"),
      currentDate: watch("currentDate"),
      dateOfBirth: watch("dateOfBirth"),
      addInvoice: watch("addInvoice"),
    };
    setDataForGeneration((prev) => ({
      ...prev,
      course: watchedFields.selectedCourse || "",
      coursePrefix: watchedFields.coursePrefix || "",
      duration: watchedFields.duration || "",
      fullName: watchedFields.fullName || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      nationality: watchedFields.nationality || "",
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      body: splitHtmlByBlocks(content),
      invoiceBody: addInvoice ? splitHtmlByBlocks(invoiceContent) : undefined,
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      course: watchedFields.selectedCourse || "",
      coursePrefix: watchedFields.coursePrefix || "",
      duration: watchedFields.duration || "",
      fullName: watchedFields.fullName || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      nationality: watchedFields.nationality || "",
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      body: splitHtmlByBlocksWithFontConvert(content),
      invoiceBody: addInvoice ? splitHtmlByBlocksWithFontConvert(invoiceContent) : undefined,
    }));
    // if()
    const hasContent = content.trim().length > 0 || invoiceContent.trim().length > 0;
    if (hasContent) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    }
  };

  const handleEditorChange = (newContent: string) => {
    setContent(newContent);

    if (isSyncingFromInputs.current) {
      isSyncingFromInputs.current = false;
      return;
    }

    isSyncingFromEditor.current = true;
    FEE_LINE_CONFIG.forEach(({ field, labels }) => {
      const extractedValue = extractFeeValueFromContent(newContent, labels);
      if (extractedValue) {
        const currentValue = getValues(field);
        if (currentValue !== extractedValue) {
          setValue(field, extractedValue, { shouldDirty: true });
        }
      }
    });
    isSyncingFromEditor.current = false;
  };
  const handleInvoiceEditorChange = (newInvoiceContent: string) => setInvoiceContent(newInvoiceContent);

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as any);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>

        <p className="admission-office-page-config-title">ADMISSION/CONFIRMATION LETTER</p>

        <form className="admission-office-page-config-form">
          <div className="form-group-fields gap">
            <div className="form-group-fields-input whose_doc">
              <AutocompleteInput
                placeholder="Whose"
                value={whoseDocAuto.query}
                onChange={whoseDocAuto.onChange}
                onSelect={(v) => {
                  whoseDocAuto.onSelect(v);
                  setValue("whose_doc", v);
                }}
                isOpen={whoseDocAuto.isOpen}
                options={whoseDocAuto.filtered}
                error={!!errors.invoiceBody}
                required
                inputProps={{
                  register: register("whose_doc", { required: addInvoice }),
                  close: whoseDocAuto.close,
                  setOpen: whoseDocAuto.onClick,
                }}
                identifier={"whose"}
              />
            </div>
            {/* <div className="form-group-fields-input whose_doc">
            <div className="form-group-fields-input-label">
              <label htmlFor="whose_doc">Whose</label>
              <div className="form-group-fields-input-label-line"></div>
            </div>

            <input
              className="whose_doc"
              id="whose_doc"
              type="text"
              {...register("whose_doc")}
              placeholder="Whose"
            />
            <div className="form-group-fields-input-svg">
              <ArrowSvg />
            </div>
          </div> */}
            <div className="form-group-fields-input coursePrefix">
              {/* <div className="form-group-fields-input-label">
              <label htmlFor="coursePrefix">Whose</label>
              <div className="form-group-fields-input-label-line"></div>
            </div> */}
              <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
              {/* <div className="form-group-fields-input-svg">
              <ArrowSvg />
            </div> */}
            </div>

            <div className="form-group-fields-invoice">
              <Controller
                name="addInvoice"
                control={control}
                render={({ field }) => <CustomSwitch label="Add Invoice" value={field.value} onChange={field.onChange} />}
              />
            </div>

            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>

              <input className="currentDate" id="currentDate" type="date" {...register("currentDate", { required: true })} />
              {errors.currentDate && <p>This field is required</p>}

              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name-nat">
                  Full Name (Nat.)<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame-nat" id="full-mame-nat" type="text" {...register("fullNameNat", { required: true })} />
              {errors.fullNameNat && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullNameNat")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">
                  Data of Birth<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth", { required: true })} />
              {errors.dateOfBirth && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <AutocompleteInput
              label="Nationality"
              value={nationalitiesAuto.query}
              onChange={nationalitiesAuto.onChange}
              onSelect={(v) => {
                nationalitiesAuto.onSelect(v);
                setValue("nationality", v);
              }}
              isOpen={nationalitiesAuto.isOpen}
              options={nationalitiesAuto.filtered}
              error={!!errors.nationality}
              required
              inputProps={{
                register: register("nationality", { required: true }),
                close: nationalitiesAuto.close,
                setOpen: nationalitiesAuto.onClick,
              }}
              identifier={"nationality"}
            />
            {/* 
            <div className="form-group-fields-input-label">
              <label htmlFor="nationality">
                Nationality<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className="nationality"
              id="nationality"
              type="text"
              {...register("nationality", { required: true })}
            />
            {errors.nationality && <p>This field is required</p>}
            <div className="form-group-fields-input-svg">
              <ArrowSvg />
            </div> 
          */}
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="formOfEducation">
                  Form of Education<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="formOfEducation" id="formOfEducation" type="text" {...register("formOfEducation", { required: true })} />
              {errors.formOfEducation && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="admissionSemester">
                  Admission Semester<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className="admissionSemester"
                id="admissionSemester"
                type="text"
                {...register("admissionSemester", { required: true })}
              />
              {errors.admissionSemester && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="intake">
                  Intake<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="intake" id="intake" type="text" {...register("intake", { required: true })} />
              {errors.intake && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="category">
                  Category<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="category" id="category" type="text" {...register("category", { required: true })} />
              {errors.category && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Select the offered course"
                value={offeredCourseAuto.query}
                onChange={offeredCourseAuto.onChange}
                onSelect={(v) => {
                  offeredCourseAuto.onSelect(v);
                  setValue("course", v);
                }}
                isOpen={offeredCourseAuto.isOpen}
                options={offeredCourseAuto.filtered}
                error={!!errors.course}
                required
                inputProps={{
                  register: register("course", { required: true }),
                  close: offeredCourseAuto.close,
                  setOpen: offeredCourseAuto.onClick,
                  setValue,
                  clearErrors,
                  name: "course",
                }}
                identifier={"offered-course-auto"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="selected-сourse">
                  Selected course<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="selected-сourse" id="selected-сourse" type="text" {...register("selectedCourse", { required: true })} />
              {errors.selectedCourse && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="medium-of-instruction">
                  Medium of Instruction<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className="medium-of-instruction"
                id="medium-of-instruction"
                type="text"
                {...register("mediumOfInstruction", { required: true })}
              />
              {errors.mediumOfInstruction && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="duration">
                  Duration<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="duration" id="duration" type="text" {...register("duration", { required: true })} />
              {errors.duration && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="tuitionFee">
                  Tuition Fee<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="tuitionFee" id="tuitionFee" type="text" {...register("tuitionFee", { required: true })} />
              {errors.tuitionFee && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="medicalInsurance">Medical Insurance</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="medicalInsurance" id="medicalInsurance" type="text" {...register("medicalInsurance")} />
              {errors.medicalInsurance && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="residencePermit">Residence Permit</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="residencePermit" id="residencePermit" type="text" {...register("residencePermit")} />
              {errors.residencePermit && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="otherExpenses">Other Expenses</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="otherExpenses" id="otherExpenses" type="text" {...register("otherExpenses")} />
              {errors.otherExpenses && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="acommodation">Acommodation</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="acommodation" id="acommodation" type="text" {...register("acommodation")} />
              {errors.acommodation && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="admissionFee">Admission Fee</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="admissionFee" id="admissionFee" type="text" {...register("admissionFee")} />
              {errors.admissionFee && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="transfer">Transfer</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="transfer" id="transfer" type="text" {...register("transfer")} />
              {errors.transfer && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="completeOneYear">Complete One Year</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="completeOneYear" id="completeOneYear" type="text" {...register("completeOneYear")} />
              {errors.completeOneYear && <p>This field is required</p>}
            </div>
          </div>
          {addInvoice && (
            <div className="form-group-fields">
              <div className="form-group-fields-input invoiceBody">
                <AutocompleteInput
                  label="Invoice details"
                  value={invoiceBodyAuto.query}
                  onChange={invoiceBodyAuto.onChange}
                  onSelect={(v) => {
                    invoiceBodyAuto.onSelect(v);
                    setValue("invoiceBody", v);
                  }}
                  isOpen={invoiceBodyAuto.isOpen}
                  options={invoiceBodyAuto.filtered}
                  error={!!errors.invoiceBody}
                  required
                  inputProps={{
                    register: register("invoiceBody", { required: addInvoice }),
                    close: invoiceBodyAuto.close,
                    setOpen: invoiceBodyAuto.onClick,
                  }}
                  identifier={"invoice"}
                />
              </div>
            </div>
          )}

          <label>Admission body</label>
          <Editor
            value={content}
            init={{
              height: 300,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: `
              body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
              p { margin: 0; }
            `,
            }}
            onEditorChange={handleEditorChange}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />

          {addInvoice && (
            <>
              <label>Invoice body</label>
              <Editor
                value={invoiceContent}
                init={{
                  height: 300,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: `
                body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                p { margin: 0; }
              `,
                }}
                onEditorChange={handleInvoiceEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
            </>
          )}

          <AOPGeneratedDocumentToolbar
            isGenerated={generatedDoc.isGenerated}
            generatedPdfUrl={generatedDoc.generatedPdfUrl}
            isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
            setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
            generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
            onDownload={generatedDoc.handleDownloadGeneratedPdf}
            onApprove={generatedDoc.handleApproveGeneratedDocument}
            onReject={generatedDoc.handleRejectGeneratedDocument}
            previewButton={
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
            }
            generateButton={
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
            }
          />
          <div className="form-group-fields">
            <div className="form-group-fields-input signature">
              <AutocompleteInput
                label="Signature"
                value={signatureAuto.query}
                defaultValue="Fedorova Anzhelika"
                onChange={signatureAuto.onChange}
                onSelect={(v) => {
                  signatureAuto.onSelect(v);
                  setValue("signature", v);
                }}
                isOpen={signatureAuto.isOpen}
                options={signatureAuto.filtered}
                error={!!errors.signature}
                required
                inputProps={{
                  register: register("signature", { required: true }),
                  close: signatureAuto.close,
                  setOpen: signatureAuto.onClick,
                }}
                identifier={"signature"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="task_id">Task Id</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="task_id" id="task_id" type="text" {...register("task_id")} />
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("task_id")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
